import React, { useState, useEffect } from 'react';
import { Modal, Table, Spin } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { TableRowSelection } from 'antd/es/table/interface';
import { useTranslation } from 'react-i18next';
import { App } from 'antd';
import { locationBarcodes } from '@/apis/inventoryv2';
import { CommonListParams } from '@/types/common';

interface SelectBarcodeProps {
  barcodes: string[] | null;
  lotStr: string;
  locationId?: string;
  open: boolean;
  values: string[];
  onConfirm: (values: string[]) => void;
  onClose: () => void;
}

interface BarcodeRecord {
  key: string;
  barcode: string;
}

const SelectBarcode: React.FC<SelectBarcodeProps> = ({
  barcodes,
  lotStr,
  locationId,
  open,
  values,
  onConfirm,
  onClose,
}) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const [dataSource, setDataSource] = useState<BarcodeRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  useEffect(() => {
    if (open) {
      if (barcodes) {
        // 使用传入的 barcodes
        setDataSource(
          barcodes.map((barcode) => ({
            key: barcode,
            barcode,
          }))
        );
      } else if (locationId && lotStr) {
        // 从接口获取条码
        fetchBarcodes();
      } else {
        setDataSource([]);
      }
      // 设置初始选中状态
      setSelectedRowKeys(values);
    }
  }, [open]);

  const fetchBarcodes = async () => {
    setLoading(true);
    try {
      const params: CommonListParams = { body: lotStr };
      const res = await locationBarcodes(locationId!, params);

      const barcodeList = (res.body || []).map((item) => item.barcode);

      setDataSource(
        barcodeList.map((barcode) => ({
          key: barcode,
          barcode,
        }))
      );
    } catch (error) {
      console.error('Failed to fetch barcodes:', error);
      notification.error({ placement: 'top', message: t('warehouse.fetchError') });
      setDataSource([]);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    onConfirm(selectedRowKeys as string[]);
  };

  const rowSelection: TableRowSelection<BarcodeRecord> = {
    selectedRowKeys,
    onChange: (selectedKeys) => {
      setSelectedRowKeys(selectedKeys);
    },
  };

  const columns: ColumnsType<BarcodeRecord> = [
    {
      title: t('counting.barcodes'),
      dataIndex: 'barcode',
      key: 'barcode',
    },
  ];

  return (
    <Modal
      title={t('warehouse.selectBarcode')}
      open={open}
      onCancel={onClose}
      onOk={handleConfirm}
      width={600}
      okText={t('common.confirm')}
      cancelText={t('common.cancel')}
    >
      <Spin spinning={loading}>
        <Table
          columns={columns}
          dataSource={dataSource}
          rowKey="key"
          rowSelection={rowSelection}
          pagination={false}
          size="small"
          scroll={{ y: 400 }}
        />
      </Spin>
    </Modal>
  );
};

export default SelectBarcode;
